import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../lib/api';
import { toast } from 'sonner';



const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    project_type: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', phone: '', project_type: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again or call us directly.');
    }
    
    setIsSubmitting(false);
  };

  const projectTypes = [
    'Commercial Construction',
    'Residential Construction',
    'Framing',
    'Drywall & Taping',
    'Painting',
    'Siding',
    'Flooring',
    'Interior Finishing',
    'Other',
  ];

  return (
    <div data-testid="contact-page">
      <Helmet>
        <title>Contact S&S Group Services | Free Quote | Vernon BC Construction Company</title>
        <meta name="description" content="Contact S&S Group Services for a free construction quote. Located at 1605 34 ST, Vernon, BC V1T 5V4. Call (236) 338-0659 or fill out our online form." />
        <meta name="keywords" content="contact construction company Vernon BC, free construction quote BC, contractor estimate Vernon, S&S Group Services contact" />
        <link rel="canonical" href="https://www.ssgroupservices.ca/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ssgroupservices.ca/contact" />
        <meta property="og:title" content="Contact S&S Group Services | Free Quote | Vernon BC" />
        <meta property="og:description" content="Get a free construction quote. Call (236) 338-0659 or use our online form. Vernon, BC." />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact S&S Group Services | Free Quote" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.ssgroupservices.ca/" },
          { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://www.ssgroupservices.ca/contact" }
        ]
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact S&S Group Services",
        "url": "https://www.ssgroupservices.ca/contact",
        "mainEntity": {
          "@type": "GeneralContractor",
          "name": "S&S Group Services",
          "telephone": "+1-236-338-0659",
          "email": "info@ssgroupservices.ca",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "1605 34 ST",
            "addressLocality": "Vernon",
            "addressRegion": "BC",
            "postalCode": "V1T 5V4",
            "addressCountry": "CA"
          }
        }
      }) }} />
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900" data-testid="contact-hero">
        <div className="absolute inset-0 grid-pattern" />
        <div className="container relative z-10">
          <div className="max-w-3xl">
            <span className="caption text-blue-400">Get In Touch</span>
            <h1 className="heading-xl text-white mt-3 mb-6">Contact Us</h1>
            <p className="text-slate-300 text-lg">
              Ready to start your construction project? Get in touch with us today for a free consultation and quote. We're here to help bring your vision to life.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white" data-testid="contact-form-section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="heading-md text-slate-900 mb-6">Let's Talk About Your Project</h2>
              <p className="text-slate-600 mb-8">
                Whether you're planning a commercial build, home renovation, or need expert construction services, our team is ready to help. Reach out to us through any of the methods below or fill out the form.
              </p>

              <div className="space-y-6 mb-12">
                <a href="tel:+12363380659" className="flex items-start gap-4 group" data-testid="contact-phone">
                  <div className="w-14 h-14 bg-blue-600 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-700 transition-colors">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-lg" style={{ fontFamily: 'Oswald, sans-serif' }}>Phone</p>
                    <p className="text-blue-600 font-semibold">(236) 338-0659</p>
                    <p className="text-slate-500 text-sm">Click to call</p>
                  </div>
                </a>

                <a href="mailto:info@ssgroupservices.ca" className="flex items-start gap-4 group" data-testid="contact-email">
                  <div className="w-14 h-14 bg-blue-600 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-700 transition-colors">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-lg" style={{ fontFamily: 'Oswald, sans-serif' }}>Email</p>
                    <p className="text-blue-600 font-semibold">info@ssgroupservices.ca</p>
                    <p className="text-slate-500 text-sm">We respond within 24 hours</p>
                  </div>
                </a>

                <div className="flex items-start gap-4" data-testid="contact-location">
                  <div className="w-14 h-14 bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-lg" style={{ fontFamily: 'Oswald, sans-serif' }}>Our Location</p>
                    <p className="text-slate-700">1605 34 ST, Vernon, BC</p>
                    <p className="text-slate-500 text-sm">Canada V1T 5V4 - Serving all of BC</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-hours">
                  <div className="w-14 h-14 bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-lg" style={{ fontFamily: 'Oswald, sans-serif' }}>Business Hours</p>
                    <p className="text-slate-700">Mon - Fri: 7:00 AM - 6:00 PM</p>
                    <p className="text-slate-700">Sat: 8:00 AM - 4:00 PM</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="border border-slate-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2551.5!2d-119.272!3d50.267!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x537dd8c2f1f3d1e3%3A0x4e4b6e6f5a5b5c5d!2s1605%2034%20St%2C%20Vernon%2C%20BC%20V1T%205V4!5e0!3m2!1sen!2sca!4v1699900000000!5m2!1sen!2sca"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="S&S Group Services Location - Vernon, BC"
                  data-testid="service-area-map"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-slate-50 p-8 lg:p-10">
                <h3 className="heading-md text-slate-900 mb-2">Request a Quote</h3>
                <p className="text-slate-600 mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>

                {submitted ? (
                  <div className="text-center py-12" data-testid="form-success">
                    <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h4 className="heading-sm text-slate-900 mb-2">Thank You!</h4>
                    <p className="text-slate-600 mb-6">Your message has been sent successfully. We'll be in touch soon.</p>
                    <Button onClick={() => setSubmitted(false)} className="btn-primary" data-testid="send-another-btn">
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wider">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wider">
                          Email *
                        </label>
                        <input
                          type="email"
                          className="form-input"
                          placeholder="your@email.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          data-testid="contact-email-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wider">
                          Phone *
                        </label>
                        <input
                          type="tel"
                          className="form-input"
                          placeholder="(236) 338-0659"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          required
                          data-testid="contact-phone-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wider">
                        Project Type *
                      </label>
                      <Select
                        value={formData.project_type}
                        onValueChange={(value) => setFormData({ ...formData, project_type: value })}
                        required
                      >
                        <SelectTrigger className="form-input h-auto" data-testid="contact-project-type-select">
                          <SelectValue placeholder="Select project type" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wider">
                        Project Details *
                      </label>
                      <textarea
                        className="form-input min-h-[150px]"
                        placeholder="Tell us about your project, timeline, and any specific requirements..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        data-testid="contact-message-textarea"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full btn-accent text-base"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>

                    <p className="text-center text-slate-500 text-sm">
                      Or call us directly at{' '}
                      <a href="tel:+12363380659" className="text-blue-600 font-semibold">
                        (236) 338-0659
                      </a>
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick CTA */}
      <section className="py-12 bg-slate-900" data-testid="contact-quick-cta">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h3 className="heading-sm text-white mb-1">Need Immediate Assistance?</h3>
              <p className="text-slate-400">Our team is ready to help with your construction needs.</p>
            </div>
            <div className="flex gap-4">
              <a href="tel:+12363380659">
                <Button size="lg" className="btn-accent" data-testid="quick-cta-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
              <a href="mailto:info@ssgroupservices.ca">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900" data-testid="quick-cta-email-btn">
                  <Mail className="w-5 h-5 mr-2" />
                  Email Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
